import type { TranslationFunction } from '@/app/components/translations/Translation';
import { IconBaggageCarryon, IconBaggageChecked, IconBaggagePersonal } from '@/app/common/icons/libraryIcons';
import type { BaggageSectionInfo } from '@lmn/lmn-components/components/Trip/shared/baggage/types.d.ts';
import { BAGGAGE_LABEL } from '@lmn/lmn-components/components/Trip/shared/baggage/types.js';

type LuggageType = 'hand' | 'cabin' | 'checked' | 'hand.total' | 'cabin.total' | 'checked.total';

const SMALL_BAG_LABEL = 'widget.trip_card.baggage_widget.small_bag';
const HAND_BAGGAGE_LABEL = 'widget.trip_card.baggage_widget.hand_luggage';
const CHECKED_BAGGAGE_LABEL = 'widget.trip_card.baggage_widget.checked_luggage';
const CHECKED_BAGGAGE_DESCRIPTION = 'widget.trip_card.baggage_widget.checked_luggage.description';
const CHECKED_BAGGAGE_DESCRIPTION_TOTAL = 'widget.trip_card.baggage_widget.checked_luggage.description.total';

/**
 * Small bag/personal item baggage information constant
 * Includes label, description, and icon for small baggage
 */
export const SMALL_BAG = {
  index: BAGGAGE_LABEL.SMALL_BAG,
  label: SMALL_BAG_LABEL,
  description: 'widget.trip_card.baggage_widget.small_bag.description',
  icon: IconBaggagePersonal,
};

/**
 * Hand baggage/cabin bag information constant
 * Includes label, description, and icon for hand/cabin baggage
 */
export const HAND_BAGGAGE = {
  index: BAGGAGE_LABEL.HAND_BAG,
  label: HAND_BAGGAGE_LABEL,
  description: 'widget.trip_card.baggage_widget.hand_luggage.description',
  icon: IconBaggageCarryon,
};

/**
 * Checked baggage/hold baggage information constant
 * Includes label, description, and icon for checked baggage
 */
export const CHECKED_BAGGAGE: BaggageSectionInfo = {
  index: BAGGAGE_LABEL.CHECKED_BAG,
  label: CHECKED_BAGGAGE_LABEL,
  description: CHECKED_BAGGAGE_DESCRIPTION,
  icon: IconBaggageChecked,
};

/**
 * MPH-932: everywhere the UI summarises the trip's luggage (results, hotel detail, flight page) the
 * airline's own allowance reads "__num__ x <bag>" — counted per passenger — while "per booking" is
 * kept for the checked bag we pre-include ourselves, which is what makes the two tellable apart.
 *
 * Gets the translation key for luggage text based on count
 * @param luggage - The type of luggage (hand, cabin, checked, total)
 * @param pieces - Number of luggage pieces
 * @param t - Translation function
 * @returns Translated text for the luggage
 */
export const getLuggageTranslation = (luggage: LuggageType, pieces: number, t: TranslationFunction): string =>
  pieces > 1
    ? t(`vacation.select.cc.luggage.${luggage}.plural`, { num: pieces.toString() })
    : t(`vacation.select.cc.luggage.${luggage}.single`);

export function getSmallBagLabel({
  numberOfLuggage,
  t,
}: {
  numberOfLuggage?: number;
  t?: TranslationFunction;
}): string {
  if (!t || !numberOfLuggage) return SMALL_BAG_LABEL;
  return getLuggageTranslation('hand', numberOfLuggage, t);
}

export function getHandBaggageLabel({
  numberOfLuggage,
  t,
}: {
  numberOfLuggage?: number;
  t?: TranslationFunction;
}): string {
  if (!t || !numberOfLuggage) return HAND_BAGGAGE_LABEL;
  return getLuggageTranslation('cabin', numberOfLuggage, t);
}

export function getCheckedBaggageLabel({
  showGroupLuggage,
  numberOfBaggagePiecesIncluded,
  numberOfLuggage,
  t,
}: {
  showGroupLuggage?: boolean;
  numberOfBaggagePiecesIncluded?: number;
  numberOfLuggage?: number;
  t?: TranslationFunction;
}): {
  label: string;
  description: string;
} {
  if (!t) {
    return {
      label: CHECKED_BAGGAGE_LABEL,
      description: CHECKED_BAGGAGE_DESCRIPTION,
    };
  }
  return {
    label: getCheckedBaggageText({
      showGroupLuggage: !!showGroupLuggage,
      numberOfBaggagePiecesIncluded: numberOfBaggagePiecesIncluded ?? 0,
      numberOfLuggage: numberOfLuggage ?? 0,
      t,
    }),
    description: t(CHECKED_BAGGAGE_DESCRIPTION_TOTAL),
  };
}

export function getCheckedBaggageText({
  showGroupLuggage,
  numberOfBaggagePiecesIncluded,
  numberOfLuggage,
  t,
}: {
  showGroupLuggage: boolean;
  numberOfBaggagePiecesIncluded: number;
  numberOfLuggage: number;
  t: TranslationFunction;
}): string {
  if (showGroupLuggage) {
    return getLuggageTranslation('checked.total', numberOfBaggagePiecesIncluded || numberOfLuggage, t);
  }
  return getLuggageTranslation('checked', numberOfLuggage, t);
}
